"use client";

import { useMemo } from "react";
import { useHintPublisher } from "@/hooks/use-hint-publisher";
import { hslHex } from "@/lib/music/chord-catalog";
import type { Hint, HintNote } from "@/contexts/hint-context";

/**
 * Octave used to materialise the progression hints on the studio piano.
 * Same convention as `use-overview-piano-hints` and `use-scale-piano-hints`
 * so the visual bar stays centred on the keyboard regardless of the
 * focus.
 */
const HINT_OCTAVE = 4;
const HINT_BASE_MIDI = HINT_OCTAVE * 12 + 12; // C4 = 60

interface ProgressionPianoHints {
  /** Pitch classes of the chord currently focused (root included). */
  chordPcs: number[];
  /** Pitch class of the chord root, painted in the warmest violet. */
  rootPc: number;
  /**
   * Actual MIDI numbers of the chord's voicing (LH bass + RH voices),
   * when available. When provided, the hint paints the keys at their
   * real played position on the keyboard (bass low, RH medium) so the
   * student visualises the spread of the hands. When omitted, all PCs
   * collapse to octave 4 (used as a fallback for minor mode where no
   * hand-picked voicing exists).
   */
  voicingMidi?: number[];
}

/**
 * Push «&nbsp;progression&nbsp;» hints to the studio piano: the pitch
 * classes of the current chord light up, the root gets the brightest
 * violet so the eye anchors to it.
 *
 * Mutually exclusive with `useOverviewPianoHints` and
 * `useScalePianoHints` via the `enabled` argument: only one harmony
 * hint source pushes at a time. Pure visual aid, no audio playback.
 *
 * Note: the hook intentionally does not surface the «&nbsp;common
 * tones with the next chord&nbsp;» idea on the piano. The
 * `ProgressionTransition` view already highlights common tones with a
 * thick violet line between the two stacks; replicating it on the
 * keyboard would dilute the «&nbsp;current chord&nbsp;» signal, the
 * piano answers a different question («&nbsp;where are my hands
 * now?&nbsp;»). See the report for the rationale.
 */
export function useProgressionPianoHints(
  hints: ProgressionPianoHints | null,
  enabled: boolean,
): void {
  const hint = useMemo<Hint | null>(() => {
    if (!enabled || !hints) {
      return null;
    }

    const vividHex = hslHex(260, 75, 65);
    const rootHex = hslHex(265, 80, 78);
    const safeRootPc = ((hints.rootPc % 12) + 12) % 12;

    const notes: HintNote[] = [];

    if (hints.voicingMidi && hints.voicingMidi.length > 0) {
      for (const midi of hints.voicingMidi) {
        const safePc = ((midi % 12) + 12) % 12;
        const isRoot = safePc === safeRootPc;

        notes.push({
          midi,
          color: isRoot ? rootHex : vividHex,
          role: isRoot ? "root" : "note",
        });
      }
    } else {
      for (const pc of hints.chordPcs) {
        const safePc = ((pc % 12) + 12) % 12;
        const midi = HINT_BASE_MIDI + safePc;
        const isRoot = safePc === safeRootPc;

        notes.push({
          midi,
          color: isRoot ? rootHex : vividHex,
          role: isRoot ? "root" : "note",
        });
      }
    }

    if (notes.length === 0) {
      return null;
    }

    // The FAB pill resolves keyboard keys via `placeVoicing`, which needs
    // explicit MIDIs at their played octave. We forward `voicingMidi`
    // (LH bass + RH voices) only, the single-octave PC fallback would
    // not represent a real hand placement.
    const voicing =
      hints.voicingMidi && hints.voicingMidi.length > 0
        ? hints.voicingMidi
        : undefined;

    return {
      source: "harmony/progression",
      midis: notes,
      ...(voicing ? { voicing } : {}),
    };
  }, [hints, enabled]);

  useHintPublisher(hint);
}
